import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationAPI } from '../utils/api';
import { ArrowLeft, Trophy, Medal, Star } from '@phosphor-icons/react';

const OrgLeaderboard = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRider, setExpandedRider] = useState(null);

  useEffect(() => {
    loadLeaderboard();
  }, [orgId]);

  const loadLeaderboard = async () => {
    try {
      const { data: lb } = await organizationAPI.getLeaderboard(orgId);
      setData(lb);
    } catch {
      console.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="shimmer h-12 w-12 rounded-full"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#9E9E9E]">Leaderboard not found</p>
      </div>
    );
  }

  const getRankStyle = (rank) => {
    if (rank === 1) return { bg: 'bg-gradient-to-r from-[#FFD700]/15 to-transparent', border: 'border-[#FFD700]', text: 'text-[#FFD700]' };
    if (rank === 2) return { bg: 'bg-gradient-to-r from-[#C0C0C0]/10 to-transparent', border: 'border-[#C0C0C0]', text: 'text-[#C0C0C0]' };
    if (rank === 3) return { bg: 'bg-gradient-to-r from-[#CD7F32]/10 to-transparent', border: 'border-[#CD7F32]', text: 'text-[#CD7F32]' };
    return { bg: 'bg-[#161616]', border: 'border-[#262626]', text: 'text-[#9E9E9E]' };
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-gradient-to-r from-[#0096FF]/10 to-[#00D4FF]/5 border-b border-[#262626]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors mb-4" data-testid="back-btn">
            <ArrowLeft size={18} /> Back
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#0096FF]/15 rounded-xl flex items-center justify-center">
              <Trophy size={28} weight="fill" className="text-[#0096FF]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-white" style={{fontFamily: 'Outfit'}} data-testid="org-name">{data.org_name}</h1>
              <p className="text-[#9E9E9E] text-sm">{data.leaderboard.length} members &bull; {data.total_shows} show{data.total_shows !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Points Table Legend */}
        <div className="mb-6 p-4 bg-[#161616] border border-[#262626] rounded-lg">
          <p className="text-xs text-[#9E9E9E] font-semibold mb-2">POINTS BY PLACING</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.points_table || {}).map(([place, pts]) => (
              <span key={place} className="text-xs text-[#555]">
                <span className="text-white font-bold">{place}{['st','nd','rd'][place-1] || 'th'}</span> = {pts}pts
              </span>
            ))}
          </div>
        </div>

        {data.leaderboard.length === 0 ? (
          <div className="text-center py-16">
            <Medal size={48} className="text-[#262626] mx-auto mb-4" />
            <p className="text-[#9E9E9E] text-lg">No results yet</p>
            <p className="text-[#555] text-sm mt-1">Points will appear as members compete in shows</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.leaderboard.map((rider) => {
              const style = getRankStyle(rider.rank);
              const comboKey = `${rider.rider_id}|${rider.horse_name || ''}`;
              const isExpanded = expandedRider === comboKey;
              return (
                <div key={comboKey}>
                  <div
                    onClick={() => setExpandedRider(isExpanded ? null : comboKey)}
                    className={`${style.bg} border ${style.border} rounded-lg p-4 sm:p-5 cursor-pointer hover:opacity-90 transition-all`}
                    data-testid={`leaderboard-rank-${rider.rank}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          rider.rank <= 3 ? `${style.bg}` : 'bg-[#0A0A0A]'
                        }`}>
                          {rider.rank <= 3 ? (
                            <Star size={24} weight="fill" className={style.text} />
                          ) : (
                            <span className="text-xl font-black text-[#9E9E9E]" style={{fontFamily: 'Outfit'}}>{rider.rank}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-lg sm:text-xl font-bold text-white" style={{fontFamily: 'Outfit'}}>{rider.rider_name}</p>
                          {rider.horse_name && <p className="text-[#0096FF] text-xs font-semibold">{rider.horse_name}</p>}
                          <p className="text-[#9E9E9E] text-xs">{rider.shows_entered} show{rider.shows_entered !== 1 ? 's' : ''} entered</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-3xl font-black ${style.text}`} style={{fontFamily: 'JetBrains Mono'}} data-testid={`points-${rider.rank}`}>
                          {rider.total_points}
                        </p>
                        <p className="text-[#9E9E9E] text-xs">points</p>
                      </div>
                    </div>
                  </div>
                  {isExpanded && rider.results.length > 0 && (
                    <div className="mt-1 ml-6 mr-2 p-3 bg-[#0A0A0A] border border-[#1a1a1a] rounded-lg space-y-1">
                      {rider.results.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[#0096FF] font-bold w-6 text-center">{r.place}{['st','nd','rd'][r.place-1] || 'th'}</span>
                            <span className="text-white">{r.class_name}</span>
                            <span className="text-[#555]">&bull; {r.show_name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-[#9E9E9E]" style={{fontFamily: 'JetBrains Mono'}}>{r.time?.toFixed(3)}s</span>
                            <span className="text-[#34C759] font-bold">+{r.points}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-[#161616] border-t border-[#262626] mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#9E9E9E]">Powered by <span className="text-[#0096FF] font-semibold">Show Master</span></p>
        </div>
      </div>
    </div>
  );
};

export default OrgLeaderboard;
